import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountSettingsService } from '../account-settings.service';
import { ChangeSubscriptionLimits, PlanWithLimits, Subscription } from '../../shared/models/plans';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Ng1ModalService } from '../../shared/ng1/ng1-modal.service';
import { UserOrPending } from '../../shared/user/user';
import { AmplitudeService } from '../../shared/services/amplitude.service';
import { markFormAsTouched } from '../../lead-generation/shared/reactive-forms/forEachElementInForm';
import { MatDialog } from '@angular/material/dialog';
import { ChangePlanConfirmationComponent } from './change-plan-confirmation/change-plan-confirmation.component';
import { Ng1LiveChatService } from '../../shared/ng1/ng1-live-chat.service';
import { ChangePlanSummaryDialogComponent } from './change-plan-summary/change-plan-summary-dialog.component';

@Component({
  selector: 'cc-choose-plan',
  templateUrl: './choose-plan.component.html',
  styleUrls: ['./choose-plan.component.scss']
})
export class ChoosePlanComponent implements OnInit {
  public panelForm: FormGroup;
  public plans$: Observable<PlanWithLimits[]>;
  public currentSubscription: Subscription;
  private users: UserOrPending[];
  public loading = false;
  private initialNumberOfUsers = 0;
  public isUserLimitReached: boolean;
  public isNotEnoughUsers: boolean;
  public userMinimum: number;

  constructor(
    private fb: FormBuilder,
    private accountSettingsService: AccountSettingsService,
    private amplitudeService: AmplitudeService,
    private ng1ModalService: Ng1ModalService,
    private matDialog: MatDialog,
    private liveChatService: Ng1LiveChatService
  ) {}

  ngOnInit() {
    this.accountSettingsService.getSubscription().subscribe(subscription => {
      this.currentSubscription = subscription;
      if (this.alreadyHasPlan()) {
        this.initialNumberOfUsers = this.currentSubscription.licenses[0].quantity;
        this.panelForm.controls.numberOfUsers.setValue(this.initialNumberOfUsers);
      }
      this.updateNumberOfUsers();
    });
    this.panelForm = this.fb.group({
      billingPeriod: this.fb.control('P1M'),
      numberOfUsers: this.fb.control('', [Validators.required, Validators.min(1)])
    });
    this.plans$ = this.subscribeToVisiblePlansChanges();
  }

  public async subscribePlan(plan: PlanWithLimits) {
    if (!this.canSubscribePlan(plan)) {
      return;
    }
    if (this.panelForm.invalid) {
      this.handleInvalidFormSubscription();
      return;
    }
    if (plan.isEnterprise) {
      this.handleEnterprisePlanSubscription();
      return;
    }

    if (this.numberOfUsers > plan.usersLimit) {
      this.handleInvalidFormSubscription();
      this.isUserLimitReached = true;
      return;
    }

    if (this.numberOfUsers < plan.usersMinimum) {
      this.panelForm.controls.numberOfUsers.setValue(plan.usersMinimum);
    }

    await this.handleValidPlanSubscription(plan, this.currentSubscription.status === 'TRIAL');
  }

  public canSubscribePlan(plan: PlanWithLimits): boolean {
    if (this.loading) {
      return false;
    }
    const modifyCurrentSubscription =
      this.alreadyHasPlan() &&
      this.currentSubscriptionIsActive() &&
      this.currentSubscription.licenses[0].planId === plan.id;

    if (modifyCurrentSubscription) {
      return this.numberOfUsersChanged();
    }
    return true;
  }

  private numberOfUsersChanged(): boolean {
    const chosenNumberOfUsers = parseInt(this.panelForm.getRawValue().numberOfUsers, 10);
    return chosenNumberOfUsers !== this.initialNumberOfUsers;
  }

  private async handleValidPlanSubscription(plan: PlanWithLimits, isTrial: boolean) {
    this.loading = true;
    this.logAmplitude('About to subscribe plan', { planId: plan.id, isTrial });

    const featuresDowngradeAccepted = await this.showDowngradeFeaturesWarningDialog(plan);
    if (!featuresDowngradeAccepted) {
      this.logAmplitude('Plan not subscribed after confirmation dialog');
      this.loading = false;
      return;
    }

    // if (this.numberOfUsers > plan.usersLimit) {
    //   this.logAmplitude('Cannot subscribe - too many users', { planId: plan.id });
    //   this.loading = false;
    //   return this.showPlanLimitsUsers(() => this.startSubscriptionPaymentFlow(plan));
    // }

    const changeSummaryAccepted = await this.showChangePlanSummaryDialog(plan, isTrial);
    if (!changeSummaryAccepted) {
      this.logAmplitude('Plan not subscribed after summary info');
      this.loading = false;
      return;
    }

    if (this.numberOfUsers < this.users.length) {
      this.logAmplitude('Cannot subscribe - too many users', { planId: plan.id });
      this.loading = false;
      return this.showPlanLimitsUsers(() => this.startSubscriptionPaymentFlow(plan));
    }

    this.startSubscriptionPaymentFlow(plan);
  }

  private async showDowngradeFeaturesWarningDialog(plan: PlanWithLimits): Promise<boolean> {
    const accountLimits: ChangeSubscriptionLimits = await this.accountSettingsService
      .getChangeSubscriptionLimits(plan.id)
      .toPromise();

    return await this.matDialog
      .open(ChangePlanConfirmationComponent, { data: {accountLimits, plan: this.currentSubscription} })
      .afterClosed()
      .toPromise();
  }

  private showChangePlanSummaryDialog(newPlan: PlanWithLimits, isTrial: boolean): Promise<boolean> {
    if (isTrial) {
      // summary info for trial accounts is shown in the Stripe Checkout form
      return Promise.resolve(true);
    }

    const { numberOfUsers: usersString, billingPeriod } = this.panelForm.getRawValue();
    const numberOfUsers = parseInt(usersString, 10);
    const data = { numberOfUsers, billingPeriod, newPlan, currentSubscription: this.currentSubscription };

    return this.matDialog
      .open(ChangePlanSummaryDialogComponent, {
        data,
        panelClass: ['mobile-full-width-dialog', 'large-width-dialog']
      })
      .afterClosed()
      .toPromise();
  }

  private startSubscriptionPaymentFlow(plan: PlanWithLimits) {
    this.logAmplitude('Plan will be subscribed', { planId: plan.id });
    this.accountSettingsService.submitSubscription({
      planId: plan.id,
      quantity: this.numberOfUsers
    });
  }

  private handleInvalidFormSubscription() {
    this.logAmplitude('Cannot subscribe - invalid form', this.panelForm.errors);
    markFormAsTouched(this.panelForm);
  }

  private handleEnterprisePlanSubscription() {
    this.logAmplitude('About to subscribe enterprise plan');
    this.liveChatService.contactSupport();
  }

  private updateNumberOfUsers() {
    this.accountSettingsService.getAllButAdministrativeUsers().subscribe((users: UserOrPending[]) => {
      const activeUsers = users.filter(s => s.state !== 'INACTIVE');
      this.users = activeUsers;
      if (!this.alreadyHasPlan()) {
        this.initialNumberOfUsers = activeUsers.length;
        this.panelForm.controls.numberOfUsers.setValue(this.initialNumberOfUsers);
      }
    });
  }

  private async showPlanLimitsUsers(callback) {
    const response = await this.ng1ModalService.lazyModalService.showPlanLimitUsersModal(
      'PLANS.TOO_MANY_ACTIVE_USERS',
      'PLANS.TOO_MANY_ACTIVE_USERS_DESCRIPTION',
      this.users,
      this.numberOfUsers
    );
    await this.accountSettingsService.updateUsersLimit(response).toPromise();
    callback();
  }

  private get numberOfUsers() {
    return this.panelForm.controls.numberOfUsers.value;
  }

  private subscribeToVisiblePlansChanges(): Observable<PlanWithLimits[]> {
    const billingPeriodCtrl = this.panelForm.controls.billingPeriod;
    const billingPeriodChanges$ = billingPeriodCtrl.valueChanges.pipe(startWith(billingPeriodCtrl.value));
    const plansChanges$ = this.accountSettingsService.getPlans();
    return combineLatest(plansChanges$, billingPeriodChanges$).pipe(
      map(([plans, billingPeriod]) => {
        const plansToShow = plans
          .filter(plan => plan.interval === billingPeriod)
          .sort((a, b) => a.pricePerUserCents - b.pricePerUserCents);
        console.log(plansToShow);
        return [...plansToShow /* EnterprisePlan // hide from Shopify's eyes */];
      })
    );
  }

  private alreadyHasPlan() {
    return this.currentSubscription && this.currentSubscription.licenses && this.currentSubscription.licenses.length;
  }

  private logAmplitude(message: string, param = {}) {
    this.amplitudeService.logEvent(`Panel:Account:Plan ${message}`, { ...this.panelForm.getRawValue(), ...param });
  }

  private currentSubscriptionIsActive(): boolean {
    if (!this.alreadyHasPlan()) {
      return false;
    }
    const { status } = this.currentSubscription;
    return status === 'ACTIVE' || status === 'TRIAL';
  }
}
